import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ChevronDown, ShieldCheck } from 'lucide-react';
import { getLocalizedText } from '../cms/localization';
import { productPath } from '../utils/slug';
import type { SupportedLang } from '../cms/localization';
import ResponsiveImage from '../components/ResponsiveImage';

interface HeroSectionProps {
  content: Record<string, unknown>;
}

const HeroSection: React.FC<HeroSectionProps> = ({ content }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as SupportedLang;
  const backgroundImage = (content.backgroundImage as string) || '/images/hero-bg.jpg';
  const foregroundImage = (content.foregroundImage as string) || '/images/yuyangguan.png';
  const eyebrow = getLocalizedText(content.eyebrow, lang, t('hero.eyebrow'));
  const title = getLocalizedText(content.title, lang, t('hero.title'));
  const subtitle = getLocalizedText(content.subtitle, lang, t('hero.subtitle'));
  const trustText = getLocalizedText(content.trustText, lang, '香港正貨・官方保養');
  const ctaText = getLocalizedText(content.ctaText, lang, t('hero.cta_explore'));
  const ctaHref = productPath(content.ctaHref as string);
  const cta2Text = getLocalizedText(content.cta2Text, lang, '瀏覽全系列');
  const cta2Href = (content.cta2Href as string) || '/products';

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden bg-brand-ink">
      {/* Background layer */}
      <div
        className="absolute inset-0 z-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      >
        <div className="absolute inset-0 bg-black/40" />
      </div>

      {/* Red ambient glow behind product */}
      <div className="absolute inset-0 z-0 flex items-center justify-center md:justify-end md:pr-[5%] pointer-events-none">
        <div
          className="w-[300px] h-[300px] md:w-[500px] md:h-[500px] rounded-full opacity-60 blur-3xl"
          style={{
            background: 'radial-gradient(circle, rgba(var(--brand-cinnabar-rgb),0.7) 0%, rgba(var(--brand-crimson-rgb),0.3) 40%, transparent 70%)',
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="flex flex-col md:flex-row items-center gap-12 md:gap-8">
          {/* Text layer - left on desktop, top on mobile */}
          <motion.div
            className="w-full md:w-[45%] text-center md:text-left order-1"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <span className="inline-block text-brand-gold font-sans tracking-[0.25em] text-xs md:text-sm mb-4 uppercase">
              {eyebrow}
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif text-brand-paper leading-tight mb-6">
              {title}
            </h1>
            <p className="text-brand-paper/80 font-sans text-base md:text-lg max-w-xl mx-auto md:mx-0 leading-relaxed mb-8">
              {subtitle}
            </p>
            {trustText && (
              <div className="flex items-center justify-center md:justify-start gap-2 text-brand-gold font-sans text-sm mb-6">
                <ShieldCheck className="w-4 h-4" />
                <span>{trustText}</span>
              </div>
            )}

            <div className="flex flex-col-reverse md:flex-row items-center gap-4">
              <Link
                to={cta2Href}
                className="w-auto text-center px-6 py-3 md:px-8 md:py-4 rounded-full md:rounded-none border border-brand-paper/80 text-brand-paper font-sans text-sm tracking-widest hover:bg-brand-paper hover:text-brand-ink transition-colors"
              >
                {cta2Text}
              </Link>
              <Link
                to={ctaHref}
                className="w-auto text-center px-6 py-3 md:px-8 md:py-4 rounded-full md:rounded-none bg-brand-cinnabar text-brand-paper font-sans text-sm tracking-widest hover:bg-brand-crimson transition-colors shadow-[0_0_30px_rgba(var(--brand-cinnabar-rgb),0.5)] md:order-last"
              >
                {ctaText}
              </Link>
            </div>
          </motion.div>

          {/* Product layer - right on desktop, bottom on mobile */}
          <motion.div
            className="w-full md:w-[55%] flex items-center justify-center order-2"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: 'easeOut', delay: 0.2 }}
          >
            <div className="relative w-full max-w-lg md:max-w-xl lg:max-w-2xl">
              <ResponsiveImage
                src={foregroundImage}
                sizesAttr="(max-width: 768px) 90vw, 55vw"
                alt={t('brand.name')}
                className="relative z-10 w-full h-auto drop-shadow-[0_0_60px_rgba(var(--brand-cinnabar-rgb),0.7)]"
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <span className="text-brand-paper/50 text-[10px] font-sans tracking-[0.3em] uppercase">
          {t('hero.scroll')}
        </span>
        <ChevronDown className="w-4 h-4 text-brand-gold" />
      </motion.div>
    </section>
  );
};

export default HeroSection;
